import { useEffect, useRef } from 'react';
import clsx from 'clsx';

/**
 * Ad placement.
 *
 * Reserves its own height so the surrounding layout never shifts when (or if)
 * a creative loads — a Cumulative Layout Shift penalty is the usual way ad
 * slots quietly wreck a dashboard's Core Web Vitals.
 *
 * Renders nothing at all unless VITE_ADSENSE_CLIENT is configured, so
 * development and self-hosted deployments stay clean. See docs/MONETISATION.md
 * for why a login-walled dashboard is a hard sell for AdSense approval and what
 * the alternatives are.
 */

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

const ADSENSE_CLIENT = import.meta.env.VITE_ADSENSE_CLIENT as string | undefined;

export type AdFormat = 'leaderboard' | 'rectangle' | 'sidebar';

const FORMAT_STYLES: Record<AdFormat, { className: string; minHeight: number }> = {
  leaderboard: { className: 'w-full', minHeight: 90 },
  rectangle: { className: 'w-full', minHeight: 250 },
  sidebar: { className: 'w-full', minHeight: 600 },
};

export function AdSlot({
  slot,
  format = 'leaderboard',
  className,
}: {
  slot?: string;
  format?: AdFormat;
  className?: string;
}) {
  const pushed = useRef(false);
  const { className: formatClass, minHeight } = FORMAT_STYLES[format];

  useEffect(() => {
    if (!ADSENSE_CLIENT || !slot || pushed.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle ?? []).push({});
      pushed.current = true;
    } catch {
      // A blocked or failed ad script must never break the page.
    }
  }, [slot]);

  if (!ADSENSE_CLIENT || !slot) return null;

  return (
    <div
      className={clsx('overflow-hidden rounded-xl border border-surface-border/60 bg-surface-raised/40', formatClass, className)}
      style={{ minHeight }}
      aria-label="Advertisement"
    >
      <ins
        className="adsbygoogle block"
        style={{ display: 'block', minHeight }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
